import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LifeBuoy, Loader2 } from 'lucide-react';

const CATEGORIES = [
  ['advice', 'Asesoría profesional', 'Professional advice'],
  ['interpretation', 'Interpretación de resultados', 'Results interpretation'],
  ['configuration_help', 'Ayuda de configuración', 'Configuration help'],
  ['new_instrument', 'Solicitar nueva evaluación', 'Request new assessment'],
  ['training_recommendation', 'Recomendación de formación', 'Training recommendation'],
  ['process_support', 'Apoyo en el proceso', 'Process support'],
  ['other', 'Otro', 'Other'],
];

const STATUS_CLS = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-slate-100 text-slate-700',
};

export function RequestHMSupport({ suite, entityRef, origin, defaultCategory, buttonVariant = 'outline', buttonSize = 'sm' }) {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(defaultCategory || 'advice');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    if (open) {
      api.get('/client/support-requests', { params: { suite } })
        .then((r) => setMine(r.data || [])).catch(() => {});
    }
  }, [open, suite]);

  const submit = async () => {
    if (subject.trim().length < 3 || message.trim().length < 3) {
      toast.error(L('Indique asunto y mensaje.', 'Provide a subject and message.'));
      return;
    }
    setBusy(true);
    try {
      await api.post('/client/support-requests', {
        suite, category, subject: subject.trim(), message: message.trim(),
        entity_ref: entityRef || null, origin: origin || window.location.pathname,
      });
      toast.success(L('Solicitud enviada al equipo CTH. Le responderán a la brevedad.',
        'Request sent to the CTH team. They will respond shortly.'));
      setSubject(''); setMessage(''); setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant={buttonVariant} size={buttonSize} onClick={() => setOpen(true)}
              data-testid={`hm-support-btn-${suite}`} className="gap-1.5">
        <LifeBuoy className="h-4 w-4" />
        {L('Solicitar apoyo de CTH', 'Request CTH support')}
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{L('Solicitar apoyo de CTH', 'Request CTH support')}</DialogTitle>
            <DialogDescription>
              {L('Opere de forma independiente cuando quiera; solicite apoyo profesional cuando lo necesite. Esta solicitud no detiene su proceso.',
                 'Operate independently when you want; ask for professional support whenever you need it. This request never blocks your process.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{L('Categoría', 'Category')}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="hm-support-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(([code, es, en]) => (
                    <SelectItem key={code} value={code}>{isES ? es : en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{L('Asunto', 'Subject')}</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200}
                     data-testid="hm-support-subject" />
            </div>
            <div>
              <Label>{L('Mensaje', 'Message')}</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={4000}
                        data-testid="hm-support-message" />
            </div>
            {mine.length > 0 && (
              <div className="rounded-md border border-border p-2 max-h-36 overflow-y-auto space-y-1" data-testid="hm-support-history">
                <p className="text-xs font-semibold text-muted-foreground">
                  {L('Mis solicitudes en este módulo', 'My requests in this module')}
                </p>
                {mine.slice(0, 6).map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate">{r.subject}</span>
                    <Badge variant="outline" className={`text-[10px] ${STATUS_CLS[r.status] || ''}`}>{r.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>{L('Cancelar', 'Cancel')}</Button>
            <Button onClick={submit} disabled={busy} data-testid="hm-support-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {L('Enviar solicitud', 'Send request')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default RequestHMSupport;
